import { LIMITS, validateConfig } from "../core/validation.js";
import { buildTilePrefix } from "../core/tileNaming.js";
import { createZipStreamWriter } from "./zipStreamWriter.js";

export function shouldIgnoreWorkerMessage(activeJobId, messageJobId) {
    return !activeJobId || activeJobId !== messageJobId;
}

function resolveOutputFormat(mimeType) {
    if (mimeType === "image/jpeg") {
        return "jpg";
    }

    if (mimeType === "image/png") {
        return "png";
    }

    return "png";
}

function triggerZipDownload(blob, fileName = "tiles.zip") {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function createImagePipeline(store) {
    let worker = null;
    let zipWriter = null;
    let lastProgressUpdate = 0;

    function ensureWorker() {
        if (!worker) {
            worker = new Worker(new URL("../workers/tileWorker.js", import.meta.url), { type: "module" });
        }

        return worker;
    }

    async function startGeneration() {
        const state = store.getState();
        const validation = validateConfig(state.config);

        if (!validation.isValid) {
            throw new Error(validation.errors.join(" "));
        }

        if (!state.imageBitmap || !state.crop || !state.imageMeta) {
            throw new Error("Load an image before generating.");
        }

        const currentWorker = ensureWorker();
        const jobId = crypto.randomUUID();
        const workerBitmap = await createImageBitmap(state.imageFile);
        const outputFormat = resolveOutputFormat(state.imageMeta.mimeType);
        const outputPrefix = buildTilePrefix(state.imageMeta.fileName);
        zipWriter = await createZipStreamWriter();

        currentWorker.onmessage = async (event) => {
            const { data } = event;
            const activeJobId = store.getState().activeJobId;

            if (shouldIgnoreWorkerMessage(activeJobId, data.jobId)) {
                return;
            }

            if (data.type === "PROGRESS") {
                const now = performance.now();
                if (now - lastProgressUpdate < 50 && data.progressPercent < 100) {
                    return;
                }
                lastProgressUpdate = now;
                store.setState({
                    workflow: "generating",
                    progressPercent: data.progressPercent,
                    statusMessage: `Generating tiles: ${data.completedTiles}/${data.totalTiles}`
                });
                return;
            }

            if (data.type === "TILE_CHUNK") {
                await zipWriter.addTile(data.fileName, data.data);
                return;
            }

            if (data.type === "JOB_COMPLETE") {
                const result = await zipWriter.close();
                zipWriter = null;
                triggerZipDownload(result.blob, `${outputPrefix}.zip`);
                store.setState({
                    workflow: "success",
                    progressPercent: 100,
                    statusMessage: `Finished ${result.entries} tiles. ZIP download started.`,
                    activeJobId: null
                });
                return;
            }

            if (data.type === "JOB_ERROR") {
                zipWriter = null;
                store.setState({
                    workflow: "error",
                    statusMessage: data.errorMessage || "Tile generation failed.",
                    activeJobId: null
                });
            }
        };

        const startPayload = {
            type: "START_JOB",
            protocolVersion: 1,
            jobId,
            source: {
                imageBitmap: workerBitmap,
                widthPx: state.imageMeta.widthPx,
                heightPx: state.imageMeta.heightPx
            },
            config: {
                tilesWide: validation.normalized.tilesWide,
                tilesHigh: validation.normalized.tilesHigh,
                tileResolution: validation.normalized.tileResolution,
                scalingMode: validation.normalized.scalingMode,
                outputFormat,
                outputPrefix
            },
            crop: state.crop,
            limits: {
                maxTiles: LIMITS.maxTiles,
                maxTotalPixels: LIMITS.maxTotalPixels
            }
        };

        store.setState({ workflow: "generating", activeJobId: jobId, progressPercent: 0, statusMessage: "Starting generation..." });
        currentWorker.postMessage(startPayload, [workerBitmap]);
    }

    function cancelActiveJob() {
        const state = store.getState();
        if (!state.activeJobId || !worker) {
            return;
        }

        worker.postMessage({
            type: "CANCEL_JOB",
            protocolVersion: 1,
            jobId: state.activeJobId
        });

        store.setState({ activeJobId: null });
    }

    return {
        startGeneration,
        cancelActiveJob
    };
}
